import { useCallback, useEffect, useRef, useState } from "react";
import SortByIcon from "../../../../../../../assets/svg/sort-by-icon";
import { TicketCard } from "./TicketCard";
import { TicketCardShimmer } from "./TicketCardShimmer";
import { HelpDeskStage, HelpDeskTicket, StageSortOption } from "../utils/types";
import { HELPDESK_TICKETS_PAGE_SIZE, SORT_OPTION_MAP } from "../utils/constant";
import { TicketStageSortingPopper } from "./TicketStageSortingPopper";
import { useGetStageTicketsQuery } from "../../../../../../../redux/services/helpdeskApi";
import PulseLoader from "../../../../../../../components/Loader/PulseLoader";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../../../../../redux/store";
import {
  DEFAULT_GRID_FILTERS,
  setHelpdeskGridSortOption,
} from "../../../../../../../redux/slices/helpdeskSlice";
import DroppableTicketStage from "./DroppableTicketStage";

interface TicketListingProps {
  stage: HelpDeskStage;
  selectedViewId?: string;
  systemNameFilter?: string;
  showSortPopper: boolean;
  onSortPopperToggle: (stageKey: string) => void;
  onSortPopperClose: () => void;
}

export const TicketListing = ({
  stage,
  selectedViewId,
  systemNameFilter,
  showSortPopper,
  onSortPopperToggle,
  onSortPopperClose,
}: TicketListingProps) => {
  const dispatch = useDispatch();
  const gridFilters = useSelector(
    (state: RootState) =>
      state.helpdesk.gridFilters[stage?.key] ?? DEFAULT_GRID_FILTERS
  );
  const pendingTicketMove = useSelector(
    (state: RootState) => state.helpdesk.pendingTicketMove
  );
  const sortOption: StageSortOption | null = gridFilters.sortOption;
  const [page, setPage] = useState(1);
  const [tickets, setTickets] = useState<HelpDeskTicket[]>(
    stage?.tickets ?? []
  );
  const [isApiMode, setIsApiMode] = useState(!!sortOption);
  const [ticketsCount, setTicketsCount] = useState(stage?.total ?? 0);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pendingRefetchRef = useRef(false);
  const lastProcessedMoveRef = useRef<number | null>(null);

  const sortParams = sortOption ? SORT_OPTION_MAP[sortOption] : null;

  const viewIdParam =
    selectedViewId && selectedViewId !== "all_tickets"
      ? selectedViewId
      : undefined;

  const { data: stageTicketsData, isFetching } = useGetStageTicketsQuery(
    {
      stage_key: stage?.key,
      page,
      limit: HELPDESK_TICKETS_PAGE_SIZE,
      ...(sortParams && {
        sort_key: sortParams?.sort_key,
        sort_order: sortParams?.sort_order,
      }),
      ...(viewIdParam && { view_id: viewIdParam }),
      ...(systemNameFilter && { system_name: systemNameFilter }),
    },
    { skip: !isApiMode }
  );

  const totalPages = isApiMode
    ? (stageTicketsData?.total_pages ?? stage?.total_pages ?? 1)
    : (stage?.total_pages ?? 1);

  const hasMore = page < totalPages;

  const resetPagination = useCallback(() => {
    scrollContainerRef?.current?.scrollTo({ top: 0, behavior: "instant" });
    setPage(1);
    setTickets([]);
  }, []);

  const handleLoadMore = useCallback(() => {
    if (isFetching || !hasMore) return;
    if (!isApiMode) {
      setIsApiMode(true);
      setPage(2);
    } else {
      setPage((prev) => prev + 1);
    }
  }, [isApiMode, isFetching, hasMore]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef?.current;
    if (!container || !hasMore || isFetching) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop > 0 && scrollHeight - scrollTop - clientHeight < 50) {
      handleLoadMore();
    }
  }, [hasMore, isFetching, handleLoadMore]);

  const handleSort = useCallback(
    (option: StageSortOption) => {
      const newSort = sortOption === option ? null : option;
      dispatch(
        setHelpdeskGridSortOption({ stageKey: stage?.key, sortOption: newSort })
      );
      resetPagination();
      setIsApiMode(true);
      onSortPopperClose();
    },
    [sortOption, resetPagination, onSortPopperClose, dispatch, stage?.key]
  );

  const handleResetSort = useCallback(() => {
    dispatch(
      setHelpdeskGridSortOption({ stageKey: stage?.key, sortOption: null })
    );
    resetPagination();
    setIsApiMode(true);
    onSortPopperClose();
  }, [resetPagination, onSortPopperClose, dispatch, stage?.key]);

  useEffect(() => {
    if (!pendingTicketMove) return;

    const isSource = pendingTicketMove.fromStageKey === stage?.key;
    const isDestination = pendingTicketMove.toStageKey === stage?.key;

    if (!isSource && !isDestination) return;

    const isNewMove = lastProcessedMoveRef.current !== pendingTicketMove?.id;

    if (isNewMove) {
      lastProcessedMoveRef.current = pendingTicketMove?.id;

      if (isSource) {
        setTickets((prev) =>
          prev?.filter(
            (t) => t?.ticket_id !== pendingTicketMove.ticket?.ticket_id
          )
        );
        setTicketsCount((prev) => Math.max(0, prev - 1));
      }

      if (isDestination) {
        setTickets((prev) => {
          const exists = prev?.some(
            (t) => t?.ticket_id === pendingTicketMove.ticket?.ticket_id
          );
          if (exists) return prev;
          return [
            { ...pendingTicketMove.ticket, stage_key: stage?.key },
            ...prev,
          ];
        });
        setTicketsCount((prev) => prev + 1);
      }
    } else if (isDestination) {
      setTickets((prev) =>
        prev?.map((t) =>
          t?.ticket_id === pendingTicketMove.ticket?.ticket_id
            ? { ...pendingTicketMove.ticket, stage_key: stage?.key }
            : t
        )
      );
    }
  }, [pendingTicketMove, stage?.key]);

  useEffect(() => {
    if (!isApiMode || !stageTicketsData?.tickets || isFetching) return;
    setTickets((prev) => {
      const newTickets = stageTicketsData?.tickets;
      if (page === 1) return newTickets;
      const existingIds = new Set(prev?.map((t) => t?.ticket_id));
      const uniqueNew = newTickets?.filter(
        (t: HelpDeskTicket) => !existingIds?.has(t?.ticket_id)
      );
      return [...prev, ...uniqueNew];
    });
    if (stageTicketsData?.total != null) {
      setTicketsCount(stageTicketsData.total);
    }
  }, [stageTicketsData, page, isApiMode, isFetching]);

  useEffect(() => {
    const container = scrollContainerRef?.current;
    if (!container || !hasMore) return;

    container?.addEventListener("scroll", handleScroll);
    return () => container?.removeEventListener("scroll", handleScroll);
  }, [handleScroll, hasMore]);

  useEffect(() => {
    if (!pendingRefetchRef.current) return;
    if (tickets?.length > 0 || isFetching) return;
    if (ticketsCount <= 0) {
      pendingRefetchRef.current = false;
      return;
    }
    pendingRefetchRef.current = false;
    scrollContainerRef?.current?.scrollTo({ top: 0, behavior: "instant" });
    setTickets([]);
    setPage(1);
    setIsApiMode(true);
  }, [tickets, ticketsCount, isFetching]);

  useEffect(() => {
    if (tickets?.length > 0 && tickets?.length < 4 && hasMore && !isFetching) {
      handleLoadMore();
    }
  }, [tickets, hasMore, isFetching, handleLoadMore]);

  useEffect(() => {
    if (!showSortPopper) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event?.target as Element;
      if (
        !target?.closest?.("[data-sort-button]") &&
        !target?.closest?.("[data-sort-dropdown]")
      ) {
        onSortPopperClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSortPopper, onSortPopperClose]);

  return (
    <div
      className="flex flex-col min-w-[300px] max-w-[300px] rounded-t-lg border-t border-x border-border-gray dark:border-border-gray-dk"
      style={{ backgroundColor: stage?.primary_hex_code }}
    >
      <div className="p-2 relative">
        <button
          data-sort-button
          className={`flex items-center gap-2.5 px-3 py-1 rounded-lg ${ticketsCount && ticketsCount > 0 ? "cursor-pointer" : "cursor-default!"}`}
          style={{ backgroundColor: stage?.secondary_hex_code }}
          onClick={() => ticketsCount > 0 && onSortPopperToggle(stage?.key)}
        >
          <span className="text-md font-semibold text-white tracking-wide">
            {stage?.title?.toUpperCase()}
          </span>
          <span className="text-sm font-medium text-white">{ticketsCount}</span>
          {ticketsCount > 0 && (
            <SortByIcon className="w-4 h-4 shrink-0 text-fixed-white dark:text-fixed-white-dk" />
          )}
        </button>
        {showSortPopper && ticketsCount !== undefined && ticketsCount > 0 && (
          <TicketStageSortingPopper
            positionClassName="top-[40px] left-0"
            activeSort={sortOption}
            onSort={handleSort}
            onReset={handleResetSort}
          />
        )}
      </div>

      <DroppableTicketStage stageKey={stage?.key}>
        <div
          ref={scrollContainerRef}
          className="overflow-y-auto custom-scrollbar h-full"
          data-ticket-stage-crm-scroll-container
        >
          <div className="flex flex-col gap-2 px-2">
            {isFetching && tickets?.length === 0
              ? Array?.from({ length: 3 })?.map((_, i) => (
                  <TicketCardShimmer key={i} />
                ))
              : tickets?.map((ticket, index) => (
                  <div
                    key={ticket?.ticket_id}
                    className={index === tickets?.length - 1 ? "pb-2" : ""}
                  >
                    <TicketCard ticket={ticket} stageKey={stage?.key} />
                  </div>
                ))}
            {isFetching && tickets?.length > 0 && (
              <div className="flex items-center justify-center pt-2 pb-1">
                <PulseLoader size={6} />
              </div>
            )}
          </div>
        </div>
      </DroppableTicketStage>
    </div>
  );
};
